<template>
	<Teleport to="body">
		<div class="modal-overlay" @click="handleOverlayClick">
			<div class="modal-container" @click.stop>
			<div class="modal-header">
				<h2>{{ modalTexts.title || $t('device_maintenance') }}</h2>
				<button class="close-btn" @click="closeModal">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
					</svg>
				</button>
			</div>

			<div class="modal-body">
				<!-- Type Selection -->
				<div class="form-section type-picker-section">
					<span class="field-label section-label">{{ $t('maintenance_type') }}</span>
					<div class="maintenance-type-segment" role="radiogroup" :aria-label="$t('maintenance_type')">
						<button
							v-for="option in maintenanceTypeOptions"
							:key="option.value"
							type="button"
							class="type-chip"
							:class="{ active: maintenanceType === option.value }"
							:aria-pressed="maintenanceType === option.value"
							:aria-label="$t(option.labelKey)"
							:title="$t(option.labelKey)"
							@click="selectMaintenanceType(option.value)"
						>
							{{ $t(option.shortLabelKey) }}
						</button>
					</div>
				</div>

				<!-- Note Section -->
				<div v-if="maintenanceType === 'note'" class="form-section">
					<label class="form-field">
						<span class="field-label">{{ $t('note_text') }}</span>
						<textarea
							v-model="currDevice.note.txt"
							class="form-textarea note-textarea"
							:placeholder="$t('enter_note_details')"
						></textarea>
					</label>

					<div class="note-info">
						<label class="form-field">
							<span class="field-label">{{ $t('date') }}</span>
							<input v-model="currDevice.note.date" class="form-input" disabled />
						</label>
						<label class="form-field">
							<span class="field-label">{{ $t('user_information') }}</span>
							<input
								v-model="currDevice.note.user_info"
								type="text"
								class="form-input"
								:placeholder="$t('enter_user_info')"
							/>
						</label>
					</div>
				</div>

				<!-- Battery Replacement Section -->
				<div v-if="maintenanceType === 'battery'" class="form-section">
					<label class="form-field">
						<span class="field-label">{{ $t('replacement_date') }}</span>
						<input v-model="currReplacement.maintenance_date" type="date" :max="todayDate" class="form-input" />
					</label>

					<label class="form-field">
						<span class="field-label">{{ $t('replaced_by_name') }}</span>
						<input
							v-model="currReplacement.technician_name"
							type="text"
							class="form-input"
							:placeholder="defaultTechnicianName || $t('enter_technician_name')"
						/>
						<span v-if="defaultTechnicianName" class="field-hint">{{ $t('prefilled_from_account') }}</span>
					</label>

					<label class="form-field">
						<span class="field-label">{{ $t('replacement_note') }}</span>
						<textarea
							v-model="currReplacement.note_text"
							class="form-textarea"
							:placeholder="$t('enter_replacement_details')"
						></textarea>
					</label>

					<!-- History Section for Battery Replacements -->
					<div class="history-section">
						<div class="history-header" @click="toggleBatteryHistory">
							<h3>{{ $t('replacement_history') }}</h3>
							<div class="history-toggle-container">
								<span v-if="batteryHistoryLoading" class="loading-spinner">Loading...</span>
								<span class="history-toggle" :class="{ expanded: showBatteryHistory }">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
										<path
											d="M6 9l6 6 6-6"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</span>
							</div>
						</div>

						<div class="history-content" v-show="showBatteryHistory">
							<div v-if="batteryHistoryLoading" class="loading-state">
								<p>{{ $t('loading_history') }}...</p>
							</div>
							<div v-else class="history-list">
								<div v-for="record in historyData" :key="record.id" class="history-item">
									<div class="history-date">
										<strong>{{ formatDate(record.maintenance_date) }}</strong>
									</div>
									<div class="history-details">
										<p>
											<strong>{{ $t('replaced_by') }}:</strong> {{ record.technician_name }}
										</p>
										<p v-if="record.note_text" class="history-note">
											<strong>{{ $t('note') }}:</strong> {{ record.note_text }}
										</p>
										<p class="history-meta">
											{{ $t('created_at') }}: {{ formatDateTime(record.created_at) }}
										</p>
									</div>
								</div>

								<div v-if="historyData.length === 0" class="no-history">
									<p>{{ $t('no_replacement_history') }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Panel Change Section -->
				<div v-if="maintenanceType === 'panel'" class="form-section">
					<label class="form-field">
						<span class="field-label">{{ $t('change_date') }}</span>
						<input v-model="currPanel.maintenance_date" type="date" :max="todayDate" class="form-input" />
					</label>

					<label class="form-field">
						<span class="field-label">{{ $t('changed_by_name') }}</span>
						<input
							v-model="currPanel.technician_name"
							type="text"
							class="form-input"
							:placeholder="defaultTechnicianName || $t('enter_technician_name')"
						/>
						<span v-if="defaultTechnicianName" class="field-hint">{{ $t('prefilled_from_account') }}</span>
					</label>

					<label class="form-field">
						<span class="field-label">{{ $t('change_note') }}</span>
						<textarea
							v-model="currPanel.note_text"
							class="form-textarea"
							:placeholder="$t('enter_change_details')"
						></textarea>
					</label>

					<!-- Panel History -->
					<div class="history-section">
						<div class="history-header" @click="togglePanelHistory">
							<h3>{{ $t('panel_change_history') }}</h3>
							<div class="history-toggle-container">
								<span v-if="panelHistoryLoading" class="loading-spinner">Loading...</span>
								<span class="history-toggle" :class="{ expanded: showPanelHistory }">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
										<path
											d="M6 9l6 6 6-6"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</span>
							</div>
						</div>

						<div class="history-content" v-show="showPanelHistory">
							<div v-if="panelHistoryLoading" class="loading-state">
								<p>{{ $t('loading_history') }}...</p>
							</div>
							<div v-else class="history-list">
								<div v-for="record in panelHistoryData" :key="record.id" class="history-item">
									<div class="history-date">
										<strong>{{ formatDate(record.maintenance_date) }}</strong>
									</div>
									<div class="history-details">
										<p>
											<strong>{{ $t('changed_by') }}:</strong> {{ record.technician_name }}
										</p>
										<p v-if="record.note_text" class="history-note">
											<strong>{{ $t('note') }}:</strong> {{ record.note_text }}
										</p>
										<p class="history-meta">
											{{ $t('created_at') }}: {{ formatDateTime(record.created_at) }}
										</p>
									</div>
								</div>

								<div v-if="panelHistoryData.length === 0" class="no-history">
									<p>{{ $t('no_panel_change_history') }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Technician Check Section -->
				<div v-if="maintenanceType === 'technician'" class="form-section">
					<label class="form-field">
						<span class="field-label">{{ $t('check_date') }}</span>
						<input v-model="currCheck.maintenance_date" type="date" :max="todayDate" class="form-input" />
					</label>

					<label class="form-field">
						<span class="field-label">{{ $t('checked_by_name') }}</span>
						<input
							v-model="currCheck.technician_name"
							type="text"
							class="form-input"
							:placeholder="defaultTechnicianName || $t('enter_technician_name')"
						/>
						<span v-if="defaultTechnicianName" class="field-hint">{{ $t('prefilled_from_account') }}</span>
					</label>

					<label class="form-field">
						<span class="field-label">{{ $t('check_note') }}</span>
						<textarea
							v-model="currCheck.note_text"
							class="form-textarea"
							:placeholder="$t('enter_check_details')"
						></textarea>
					</label>

					<!-- Technician Check History -->
					<div class="history-section">
						<div class="history-header" @click="toggleCheckHistory">
							<h3>{{ $t('technician_check_history') }}</h3>
							<div class="history-toggle-container">
								<span v-if="checkHistoryLoading" class="loading-spinner">Loading...</span>
								<span class="history-toggle" :class="{ expanded: showCheckHistory }">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
										<path
											d="M6 9l6 6 6-6"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</span>
							</div>
						</div>

						<div class="history-content" v-show="showCheckHistory">
							<div v-if="checkHistoryLoading" class="loading-state">
								<p>{{ $t('loading_history') }}...</p>
							</div>
							<div v-else class="history-list">
								<div v-for="record in checkHistoryData" :key="record.id" class="history-item">
									<div class="history-date">
										<strong>{{ formatDate(record.maintenance_date) }}</strong>
									</div>
									<div class="history-details">
										<p>
											<strong>{{ $t('checked_by') }}:</strong> {{ record.technician_name }}
										</p>
										<p v-if="record.note_text" class="history-note">
											<strong>{{ $t('note') }}:</strong> {{ record.note_text }}
										</p>
										<p class="history-meta">
											{{ $t('created_at') }}: {{ formatDateTime(record.created_at) }}
										</p>
									</div>
								</div>

								<div v-if="checkHistoryData.length === 0" class="no-history">
									<p>{{ $t('no_technician_check_history') }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="modal-footer">
				<button class="btn btn-secondary" @click="closeModal">
					{{ modalTexts.cancelText || $t('cancel') }}
				</button>
				<button class="btn btn-primary" @click="handleSave">
					{{ modalTexts.confirmText || $t('save') }}
				</button>
			</div>
			</div>
		</div>
	</Teleport>
</template>

<script>
import { ddmmyyhhmm } from '../../../filters'
import { mapGetters } from 'vuex'
import Teleport from 'vue2-teleport'
import { lockPageScroll, unlockPageScroll } from '@/modules/common/utils/scrollLock'
import {
	MAINTENANCE_TYPE_OPTIONS,
	getLoggedUserDisplayName,
	getTodayIsoDate,
} from '../utils/maintenanceUtils'

const HISTORY_VISIBILITY = {
	battery: 'showBatteryHistory',
	panel: 'showPanelHistory',
	technician: 'showCheckHistory',
}

export default {
	name: 'CombinedMaintenanceModal',
	props: {
		currDevice: {
			type: Object,
			required: true,
			default: () => ({
				note: {
					txt: '',
					date: '',
					user_info: ''
				}
			})
		},
		currReplacement: {
			type: Object,
			required: true,
			default: () => ({
				replacement_date: '',
				replaced_by_name: '',
				replacement_note: '',
				created_at: null,
				updated_by: ''
			})
		},
		currPanel: {
			type: Object,
			required: true,
			default: () => ({
				change_date: '',
				changed_by_name: '',
				change_note: '',
				created_at: null,
				updated_by: ''
			})
		},
		currCheck: {
			type: Object,
			required: true,
			default: () => ({
				check_date: '',
				checked_by_name: '',
				check_note: '',
				created_at: null,
				updated_by: ''
			})
		},
		modalTexts: {
			type: Object,
			default: () => ({
				title: 'Device Maintenance',
				confirmText: 'Save',
				cancelText: 'Cancel'
			})
		},
		historyData: {
			type: Array,
			default: () => []
		},
		panelHistoryData: {
			type: Array,
			default: () => []
		},
		checkHistoryData: {
			type: Array,
			default: () => []
		},
		batteryHistoryLoading: {
			type: Boolean,
			default: false
		},
		panelHistoryLoading: {
			type: Boolean,
			default: false
		},
		checkHistoryLoading: {
			type: Boolean,
			default: false
		},
		initialType: {
			type: String,
			default: 'note'
		}
	},
	data() {
		return {
			showBatteryHistory: false,
			showPanelHistory: false,
			showCheckHistory: false,
			maintenanceType: this.initialType,
			// Track initial state to detect changes
			initialState: {
				note: null,
				battery: null,
				panel: null,
				technician: null
			}
		}
	},
	computed: {
		...mapGetters(['loggedUser']),
		maintenanceTypeOptions() {
			return MAINTENANCE_TYPE_OPTIONS
		},
		defaultTechnicianName() {
			return getLoggedUserDisplayName(this.loggedUser)
		},
		todayDate() {
			return getTodayIsoDate()
		},
		// Check if note has been modified from initial state
		hasNoteChanged() {
			if (!this.initialState.note) return false
			return JSON.stringify(this.currDevice.note) !== JSON.stringify(this.initialState.note)
		},
		// Check if battery data has been modified
		hasBatteryChanged() {
			if (!this.initialState.battery) return false
			return JSON.stringify(this.currReplacement) !== JSON.stringify(this.initialState.battery)
		},
		// Check if panel data has been modified
		hasPanelChanged() {
			if (!this.initialState.panel) return false
			return JSON.stringify(this.currPanel) !== JSON.stringify(this.initialState.panel)
		},
		// Check if technician data has been modified
		hasCheckChanged() {
			if (!this.initialState.technician) return false
			return JSON.stringify(this.currCheck) !== JSON.stringify(this.initialState.technician)
		},
	},
	mounted() {
		// Set current date as default for note date
		const today = ddmmyyhhmm(new Date())

		if (!this.currDevice.note.date) {
			this.currDevice.note.date = today
		}

		const todayIso = this.todayDate
		if (!this.currReplacement.maintenance_date) {
			this.currReplacement.maintenance_date = todayIso
		}
		if (!this.currPanel.maintenance_date) {
			this.currPanel.maintenance_date = todayIso
		}
		if (!this.currCheck.maintenance_date) {
			this.currCheck.maintenance_date = todayIso
		}

		this.applyDefaultTechnicianNames()
		this.applyDefaultNoteUserInfo()

		// Snapshot after defaults so change detection reflects user edits only
		this.initialState = {
			note: JSON.parse(JSON.stringify(this.currDevice.note)),
			battery: JSON.parse(JSON.stringify(this.currReplacement)),
			panel: JSON.parse(JSON.stringify(this.currPanel)),
			technician: JSON.parse(JSON.stringify(this.currCheck))
		}

		lockPageScroll()

		// Add escape key listener
		document.addEventListener('keydown', this.handleEscapeKey)
	},
	beforeDestroy() {
		unlockPageScroll()
		document.removeEventListener('keydown', this.handleEscapeKey)
	},
	methods: {
		isNoteDirty() {
			const initial = this.initialState.note || {}
			const current = this.currDevice.note || {}
			const initialText = (initial.txt || '').trim()
			const currentText = (current.txt || '').trim()
			const initialUser = (initial.user_info || initial.userInfo || '').trim()
			const currentUser = (current.user_info || current.userInfo || '').trim()

			return currentText !== initialText
				|| currentUser !== initialUser
				|| (current.date || '') !== (initial.date || '')
		},
		selectMaintenanceType(value) {
			this.maintenanceType = value
		},
		applyDefaultTechnicianNames() {
			const defaultName = this.defaultTechnicianName
			if (!defaultName) return
			if (!this.currReplacement.technician_name || !this.currReplacement.technician_name.trim()) {
				this.currReplacement.technician_name = defaultName
			}
			if (!this.currPanel.technician_name || !this.currPanel.technician_name.trim()) {
				this.currPanel.technician_name = defaultName
			}
			if (!this.currCheck.technician_name || !this.currCheck.technician_name.trim()) {
				this.currCheck.technician_name = defaultName
			}
		},
		applyDefaultNoteUserInfo() {
			const defaultName = this.defaultTechnicianName
			if (!defaultName || !this.currDevice.note) return
			if (!this.currDevice.note.user_info || !this.currDevice.note.user_info.trim()) {
				this.currDevice.note.user_info = defaultName
			}
		},
		async toggleBatteryHistory() {
			this.toggleHistory('battery')
		},

		async togglePanelHistory() {
			this.toggleHistory('panel')
		},

		async toggleCheckHistory() {
			this.toggleHistory('technician')
		},

		toggleHistory(maintenanceType) {
			const visibilityKey = HISTORY_VISIBILITY[maintenanceType]
			if (!visibilityKey) return

			this[visibilityKey] = !this[visibilityKey]
			if (this[visibilityKey]) {
				this.$emit('loadMaintenanceHistory', maintenanceType)
			}
		},

		formatDate(dateString) {
			const date = new Date(dateString)
			return date.toLocaleDateString()
		},

		formatDateTime(dateString) {
			const date = new Date(dateString)
			return date.toLocaleString()
		},

		handleSave() {
			const saveActions = {
				note: () => {
					if (!this.isNoteDirty()) {
						this.$emit('validationError', this.$t('no_data_entered'))
						return false
					}
					this.saveNote()
					return true
				},
				battery: () => this.saveReplacement(),
				panel: () => this.savePanelChange(),
				technician: () => this.saveTechnicianCheck(),
			}

			const saveAction = saveActions[this.maintenanceType]
			if (!saveAction || !saveAction()) {
				return
			}

			this.closeModal()
		},

		saveNote() {
			// Always save note changes, even if empty (allows clearing)
			this.$emit('saveDevice', this.currDevice)
		},

		saveReplacement() {
			return this.saveMaintenanceRecord(this.currReplacement, 'battery')
		},

		savePanelChange() {
			return this.saveMaintenanceRecord(this.currPanel, 'panel')
		},

		saveTechnicianCheck() {
			return this.saveMaintenanceRecord(this.currCheck, 'technician')
		},

		saveMaintenanceRecord(record, maintenanceType) {
			const hasAnyData = record.technician_name.trim() || record.note_text.trim()

			if (!hasAnyData) {
				this.$emit('validationError', this.$t('no_data_entered'))
				return false
			}

			if (!record.technician_name.trim()) {
				this.$emit('validationError', this.$t('technician_name_required'))
				return false
			}

			if (!record.created_at) {
				record.created_at = new Date().toISOString()
			}

			record.maintenance_type = maintenanceType
			this.$emit('saveMaintenanceRecord', { maintenanceType, data: record })
			return true
		},

		closeModal() {
			this.$emit('closeModal')
		},

		handleOverlayClick() {
			this.closeModal()
		},

		handleEscapeKey(event) {
			if (event.key === 'Escape') {
				this.closeModal()
			}
		}
	},
	components: {
		Teleport,
	},
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.modal-overlay {
	position: fixed;
	inset: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.35);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10050;
	-webkit-backdrop-filter: blur(10px);
	backdrop-filter: blur(10px);
	overflow: hidden;
	overscroll-behavior: contain;
	padding: 20px;
	box-sizing: border-box;
}

.modal-container {
	background: white;
	border-radius: 12px;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
	width: 90%;
	max-width: 600px;
	max-height: 90vh;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
	from {
		opacity: 0;
		transform: translateY(-20px) scale(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px 24px 0 24px;
	border-bottom: 1px solid #e9ecef;
	margin-bottom: 0;

	h2 {
		margin: 0;
		font-size: 1.5rem;
		font-weight: 600;
		color: #333;
	}

	.close-btn {
		background: none;
		border: none;
		cursor: pointer;
		padding: 8px;
		border-radius: 6px;
		color: #666;
		transition: all 0.2s ease;

		&:hover {
			background-color: #f8f9fa;
			color: #333;
		}
	}
}

.modal-body {
	flex: 1;
	overflow-y: auto;
	padding: 24px;
}

.form-section {
	margin-bottom: 24px;

	&.type-picker-section {
		margin-bottom: 16px;
	}

	.section-label {
		display: block;
		margin-bottom: 8px;
		font-size: 13px;
	}

	.maintenance-type-segment {
		display: inline-flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px;
		max-width: 100%;
		padding: 4px;
		background: #eef1f4;
		border-radius: 10px;
		border: 1px solid #e2e6ea;
	}

	.type-chip {
		flex: 0 0 auto;
		margin: 0;
		padding: 6px 10px;
		min-height: 0;
		border: none;
		border-radius: 7px;
		background: transparent;
		color: #5a6570;
		font-size: 12px;
		font-weight: 600;
		line-height: 1.2;
		white-space: nowrap;
		cursor: pointer;
		transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

		&:hover:not(.active) {
			background: rgba(255, 255, 255, 0.65);
			color: #389ceb;
		}

		&.active {
			background: #fff;
			color: #389ceb;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
		}

		&:focus-visible {
			outline: 2px solid #389ceb;
			outline-offset: 1px;
		}
	}

	.field-hint {
		margin-top: 6px;
		font-size: 12px;
		color: #6c757d;
		font-style: italic;
	}

	.form-field {
		display: flex;
		flex-direction: column;
		margin-bottom: 20px;

		.field-label {
			font-weight: 500;
			color: #555;
			margin-bottom: 6px;
			font-size: 14px;
		}

		.form-input,
		.form-select {
			padding: 12px 16px;
			border: 1.5px solid #e1e5e9;
			border-radius: 8px;
			font-size: 14px;
			transition: all 0.2s ease;
			background-color: white;

			&:focus {
				outline: none;
				border-color: #007bff;
				box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
			}

			&:disabled {
				background-color: #f8f9fa;
				color: #6c757d;
				cursor: not-allowed;
			}
		}

		.form-textarea {
			padding: 12px 16px;
			border: 1.5px solid #e1e5e9;
			border-radius: 8px;
			font-size: 14px;
			font-family: inherit;
			resize: vertical;
			min-height: 100px;
			transition: all 0.2s ease;

			&:focus {
				outline: none;
				border-color: #007bff;
				box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
			}

			&.note-textarea {
				min-height: 150px;
			}
		}
	}

	.note-info {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
		margin-top: 20px;
	}
}

.history-section {
	margin-top: 24px;
	border-top: 1px solid #e9ecef;
	padding-top: 20px;

	.history-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		cursor: pointer;
		padding: 12px 16px;
		border-radius: 8px;
		transition: all 0.2s ease;

		&:hover {
			background-color: #f8f9fa;
		}

		h3 {
			margin: 0;
			color: #333;
			font-size: 16px;
			font-weight: 600;
		}

		.history-toggle-container {
			display: flex;
			align-items: center;
			gap: 8px;

			.loading-spinner {
				font-size: 12px;
				color: #007bff;
			}

			.history-toggle {
				display: flex;
				align-items: center;
				color: #666;
				transition: transform 0.2s ease;

				&.expanded {
					transform: rotate(180deg);
				}
			}
		}
	}

	.history-content {
		margin-top: 12px;
		max-height: 300px;
		overflow-y: auto;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-thumb {
			background-color: #d1d5db;
			border-radius: 3px;
		}
	}

	.loading-state {
		text-align: center;
		padding: 20px;
		color: #666;
		font-style: italic;
	}

	.history-list {
		.history-item {
			display: flex;
			gap: 16px;
			padding: 16px;
			margin-bottom: 12px;
			background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
			border-radius: 8px;
			border-left: 4px solid #007bff;
			transition: all 0.2s ease;

			&:hover {
				transform: translateX(2px);
				box-shadow: 0 4px 12px rgba(0, 123, 255, 0.1);
			}

			.history-date {
				min-width: 100px;
				color: #007bff;
				font-size: 13px;
				font-weight: 600;
			}

			.history-details {
				flex: 1;

				p {
					margin: 0 0 6px 0;
					font-size: 14px;
					color: #333;
					line-height: 1.4;

					&.history-note {
						color: #666;
						font-style: italic;
						margin: 10px 0;
						padding: 8px 12px;
						background-color: rgba(0, 123, 255, 0.05);
						border-radius: 6px;
						border-left: 2px solid #007bff;
					}

					&.history-meta {
						font-size: 12px;
						color: #999;
						margin-top: 10px;
					}

					strong {
						font-weight: 600;
					}
				}
			}
		}

		.no-history {
			text-align: center;
			padding: 40px 20px;
			color: #666;
			font-style: italic;
		}
	}
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 20px 24px 24px;
	border-top: 1px solid #e9ecef;
	background-color: #fafbfc;

	.btn {
		padding: 10px 20px;
		border-radius: 6px;
		font-weight: 500;
		font-size: 14px;
		cursor: pointer;
		border: none;
		transition: all 0.2s ease;

		&.btn-secondary {
			background-color: #6c757d;
			color: white;

			&:hover {
				background-color: #5a6268;
			}
		}

		&.btn-primary {
			background-color: #007bff;
			color: white;

			&:hover {
				background-color: #0056b3;
				transform: translateY(-1px);
				box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
			}
		}
	}
}

@media (max-width: 768px) {
	.modal-container {
		width: 95%;
		max-height: 95vh;
	}

	.modal-header,
	.modal-body,
	.modal-footer {
		padding: 16px;
	}

	.note-info {
		grid-template-columns: 1fr;
	}
}
</style>
